<template>
  <div class="gestion-kpis-container p-4">
    <div class="mb-3">
      <vx-tooltip text="Volver al menú principal de Control de Actividades" position="right">
        <vs-button icon="arrow_back" size="small" type="border" color="dark" @click="$router.push('/actividades-control')">
          Regresar al Control
        </vs-button>
      </vx-tooltip>
    </div>
    <vx-card title="Gestión de KPIs por Empleado">
      <!-- Selección de empleado -->
      <div class="flex flex-wrap items-center mb-6 gap-3">
        <div class="w-full md:w-1/3">
          <label class="text-sm font-semibold">Empleado</label>
          <v-select
            v-model="empleadoSeleccionado"
            :options="empleados"
            :reduce="emp => emp.idusuario"
            label="nombre_completo"
            placeholder="Seleccione un empleado"
            class="w-full"
            @input="cargarKpisEmpleado"
          />
        </div>
        <div class="w-full md:w-1/4 flex items-end">
          <vx-tooltip v-if="empleadoSeleccionado" text="Asignar un nuevo límite o meta de KPI al colaborador">
            <vs-button
              @click="abrirModalNuevoKpi"
              color="primary"
              icon="add"
            >
              Agregar KPI
            </vs-button>
          </vx-tooltip>
        </div>
      </div>

      <!-- Tabla de KPIs -->
      <div v-if="empleadoSeleccionado">
        <h3 class="font-bold text-lg mb-4">KPIs Configurados</h3>
        <vs-table :data="kpisEmpleado" stripe>
          <template slot="thead">
            <vs-th>Categoría</vs-th>
            <vs-th>Mínimo Diario</vs-th>
            <vs-th>Máximo Diario</vs-th>
            <vs-th>Mínimo Mensual</vs-th>
            <vs-th>Máximo Mensual</vs-th>
            <vs-th>Mínimo Anual</vs-th>
            <vs-th>Máximo Anual</vs-th>
            <vs-th>Creado Por</vs-th>
            <vs-th>Acciones</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="(kpi, index) in data" :key="index">
              <vs-td>
                <vs-chip :color="(kpi.categoria && kpi.categoria.color) || 'primary'">
                  {{ (kpi.categoria && kpi.categoria.nombre_categoria) || 'General' }}
                </vs-chip>
              </vs-td>
              <vs-td>{{ kpi.limite_diario_minimo }}</vs-td>
              <vs-td>{{ kpi.limite_diario_maximo || '-' }}</vs-td>
              <vs-td>{{ kpi.limite_mensual_minimo }}</vs-td>
              <vs-td>{{ kpi.limite_mensual_maximo || '-' }}</vs-td>
              <vs-td>{{ kpi.limite_anual_minimo }}</vs-td>
              <vs-td>{{ kpi.limite_anual_maximo || '-' }}</vs-td>
              <vs-td>
                <span class="text-sm">
                  {{ kpi.creado_por ? kpi.creado_por.nombres : '-' }}
                </span>
              </vs-td>
              <vs-td>
                <div class="flex gap-2">
                  <vx-tooltip text="Editar metas de este KPI">
                    <vs-button
                      size="small"
                      icon="edit"
                      @click="editarKpi(kpi)"
                      color="warning"
                      type="flat"
                    />
                  </vx-tooltip>
                  <vx-tooltip text="Eliminar KPI">
                    <vs-button
                      size="small"
                      icon="delete"
                      @click="eliminarKpi(kpi.id_kpi)"
                      color="danger"
                      type="flat"
                    />
                  </vx-tooltip>
                </div>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>

      <div v-else class="text-center p-8 text-gray-500">
        <vs-icon icon="assessment" size="large" class="mb-2" />
        <p>Seleccione un empleado para gestionar sus KPIs</p>
      </div>
    </vx-card>

    <!-- Modal crear/editar KPI -->
    <vs-popup
      title="Configurar KPI"
      :active.sync="modalKpi"
    >
      <div class="p-4">
        <div class="grid grid-cols-1 gap-4">
          <div>
            <label class="text-sm font-semibold">Categoría</label>
            <v-select
              v-model="kpiForm.categoria"
              :options="categoriasDisponibles"
              label="nombre_categoria"
              placeholder="Seleccione categoría (opcional)"
              class="w-full"
            />
            <small class="text-gray-500">Dejar vacío para KPI general</small>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-semibold">Mínimo Diario *</label>
              <vs-input-number
                v-model="kpiForm.limite_diario_minimo"
                :min="0"
                class="w-full"
              />
            </div>
            <div>
              <label class="text-sm font-semibold">Máximo Diario</label>
              <vs-input-number
                v-model="kpiForm.limite_diario_maximo"
                :min="0"
                class="w-full"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-semibold">Mínimo Mensual *</label>
              <vs-input-number
                v-model="kpiForm.limite_mensual_minimo"
                :min="0"
                class="w-full"
              />
            </div>
            <div>
              <label class="text-sm font-semibold">Máximo Mensual</label>
              <vs-input-number
                v-model="kpiForm.limite_mensual_maximo"
                :min="0"
                class="w-full"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-sm font-semibold">Mínimo Anual *</label>
              <vs-input-number
                v-model="kpiForm.limite_anual_minimo"
                :min="0"
                class="w-full"
              />
            </div>
            <div>
              <label class="text-sm font-semibold">Máximo Anual</label>
              <vs-input-number
                v-model="kpiForm.limite_anual_maximo"
                :min="0"
                class="w-full"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <vs-button @click="modalKpi = false" color="danger" type="flat">
            Cancelar
          </vs-button>
          <vs-button @click="guardarKpi" color="primary">
            {{ kpiForm.id_kpi ? 'Actualizar' : 'Guardar' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { mapState } from 'vuex'

export default {
  name: 'GestionKPIs',
  components: {
    vSelect
  },
  data() {
    return {
      empleados: [],
      empleadoSeleccionado: null,
      kpisEmpleado: [],
      categoriasDisponibles: [],
      modalKpi: false,
      kpiForm: {
        id_kpi: null,
        categoria: null,
        limite_diario_minimo: 1,
        limite_diario_maximo: null,
        limite_mensual_minimo: 20,
        limite_mensual_maximo: null,
        limite_anual_minimo: 240,
        limite_anual_maximo: null
      }
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    usuarioId() {
      return this.auth ? this.auth.idusuario : null
    }
  },
  mounted() {
    this.cargarEmpleados()
    this.cargarCategorias()
  },
  methods: {
    async cargarEmpleados() {
      try {
        // Usar el nuevo endpoint que solo trae usuarios del grupo 36 activos
        const response = await this.$http.get(this.$server_url + 'actividades-control/empleados/activos')
        this.empleados = response.data
      } catch (error) {
        console.error('Error al cargar empleados:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudieron cargar los empleados',
          color: 'danger'
        })
      }
    },
    async cargarCategorias() {
      try {
        const response = await this.$http.get(this.$server_url + 'actividades-control/categorias')
        this.categoriasDisponibles = response.data || []
      } catch (error) {
        console.error('Error al cargar categorías:', error)
      }
    },
    async cargarKpisEmpleado() {
      if (!this.empleadoSeleccionado) return
      this.$vs.loading()
      try {
        const response = await this.$http.get(this.$server_url + `actividades-control/kpis/usuario/${this.empleadoSeleccionado}`)
        this.kpisEmpleado = response.data || []
      } catch (error) {
        console.error('Error al cargar KPIs:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudieron cargar los KPIs',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    abrirModalNuevoKpi() {
      this.kpiForm = {
        id_kpi: null,
        categoria: null,
        limite_diario_minimo: 1,
        limite_diario_maximo: null,
        limite_mensual_minimo: 20,
        limite_mensual_maximo: null,
        limite_anual_minimo: 240,
        limite_anual_maximo: null
      }
      this.modalKpi = true
    },
    editarKpi(kpi) {
      this.kpiForm = {
        id_kpi: kpi.id_kpi,
        categoria: kpi.categoria,
        limite_diario_minimo: kpi.limite_diario_minimo,
        limite_diario_maximo: kpi.limite_diario_maximo,
        limite_mensual_minimo: kpi.limite_mensual_minimo,
        limite_mensual_maximo: kpi.limite_mensual_maximo,
        limite_anual_minimo: kpi.limite_anual_minimo,
        limite_anual_maximo: kpi.limite_anual_maximo
      }
      this.modalKpi = true
    },
    async guardarKpi() {
      if (!this.empleadoSeleccionado) {
        this.$vs.notify({
          title: 'Atención',
          text: 'Debe seleccionar un empleado',
          color: 'warning'
        })
        return
      }

      try {
        const payload = {
          idusuario: this.empleadoSeleccionado,
          id_categoria: (this.kpiForm.categoria && this.kpiForm.categoria.id_categoria) || null,
          limite_diario_minimo: this.kpiForm.limite_diario_minimo,
          limite_diario_maximo: this.kpiForm.limite_diario_maximo,
          limite_mensual_minimo: this.kpiForm.limite_mensual_minimo,
          limite_mensual_maximo: this.kpiForm.limite_mensual_maximo,
          limite_anual_minimo: this.kpiForm.limite_anual_minimo,
          limite_anual_maximo: this.kpiForm.limite_anual_maximo,
          creado_por: this.usuarioId
        }

        if (this.kpiForm.id_kpi) {
          await this.$http.put(this.$server_url + `actividades-control/kpis/${this.kpiForm.id_kpi}`, payload)
          this.$vs.notify({
            title: 'Éxito',
            text: 'KPI actualizado correctamente',
            color: 'success'
          })
        } else {
          await this.$http.post(this.$server_url + 'actividades-control/kpis', payload)
          this.$vs.notify({
            title: 'Éxito',
            text: 'KPI creado correctamente',
            color: 'success'
          })
        }

        this.modalKpi = false
        this.cargarKpisEmpleado()
      } catch (error) {
        console.error('Error al guardar KPI:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudo guardar el KPI',
          color: 'danger'
        })
      }
    },
    eliminarKpi(idKpi) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmar eliminación',
        text: '¿Está seguro de eliminar este KPI?',
        accept: async () => {
          try {
            // Implementar endpoint de eliminación si es necesario
            this.$vs.notify({
              title: 'Información',
              text: 'Funcionalidad de eliminación pendiente',
              color: 'warning'
            })
          } catch (error) {
            console.error('Error al eliminar KPI:', error)
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.gestion-kpis-container {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
